import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import reviewService from '../../../services/reviewService';

// Generate mock availability for next 14 days
const generateAvailability = (seed) => {
  const days = [];
  const today = new Date();
  for (let i = 0; i < 14; i++) {
    const d = new Date(today);
    d?.setDate(today?.getDate() + i);
    const dayOfWeek = d?.getDay();
    // Sundays always unavailable; some days busy based on seed
    const isWeekend = dayOfWeek === 0;
    const isBusy = !isWeekend && ((seed + i) % 5 === 0);
    days?.push({
      date: d,
      status: isWeekend ? 'unavailable' : isBusy ? 'busy' : 'available',
    });
  }
  return days;
};

const StarRating = ({ rating, size = 14 }) => {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5]?.map((i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill={i <= full ? '#F59E0B' : (i === full + 1 && half ? 'url(#half)' : 'none')} stroke="#F59E0B" strokeWidth="1.5">
          {i === full + 1 && half && (
            <defs>
              <linearGradient id="half">
                <stop offset="50%" stopColor="#F59E0B" />
                <stop offset="50%" stopColor="transparent" />
              </linearGradient>
            </defs>
          )}
          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
        </svg>
      ))}
    </div>
  );
};

const AvailabilityCalendar = ({ seed }) => {
  const days = generateAvailability(seed);
  const dayLabels = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  return (
    <div>
      <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">Disponibilités (14 prochains jours)</p>
      <div className="grid grid-cols-7 gap-1">
        {dayLabels?.map((d, i) => (
          <div key={i} className="text-center text-xs text-text-secondary/60 font-medium pb-1">{d}</div>
        ))}
        {days?.map((day, i) => {
          const isToday = i === 0;
          const colorMap = {
            available: 'bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer',
            busy: 'bg-amber-100 text-amber-600',
            unavailable: 'bg-slate-100 text-slate-400',
          };
          return (
            <div
              key={i}
              title={day?.status === 'available' ? 'Disponible' : day?.status === 'busy' ? 'Occupé' : 'Indisponible'}
              className={`aspect-square flex items-center justify-center rounded-lg text-xs font-medium transition-colors ${colorMap?.[day?.status]} ${isToday ? 'ring-2 ring-brand-gold ring-offset-1' : ''}`}
            >
              {day?.date?.getDate()}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3 mt-2">
        <span className="flex items-center gap-1 text-xs text-text-secondary"><span className="w-2.5 h-2.5 rounded-sm bg-green-100 border border-green-300 inline-block" />Disponible</span>
        <span className="flex items-center gap-1 text-xs text-text-secondary"><span className="w-2.5 h-2.5 rounded-sm bg-amber-100 border border-amber-300 inline-block" />Occupé</span>
        <span className="flex items-center gap-1 text-xs text-text-secondary"><span className="w-2.5 h-2.5 rounded-sm bg-slate-100 border border-slate-300 inline-block" />Indisponible</span>
      </div>
    </div>
  );
};

const ProfessionalProfileCard = ({ professional, onSelect, isSelected }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedReview, setExpandedReview] = useState(null);
  const [liveAggregate, setLiveAggregate] = useState(null);
  const [liveReviews, setLiveReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);

  // Fetch live aggregate when reviews tab is opened
  useEffect(() => {
    if (activeTab === 'reviews' && !liveAggregate) {
      fetchLiveData();
    }
  }, [activeTab]);

  const fetchLiveData = async () => {
    setLoadingReviews(true);
    const [aggResult, reviewsResult] = await Promise.all([
      reviewService?.getProfessionalRatingAggregate(professional?.id),
      reviewService?.getProfessionalReviews(professional?.id, 10),
    ]);
    if (aggResult?.data) setLiveAggregate(aggResult?.data);
    if (reviewsResult?.data?.length > 0) setLiveReviews(reviewsResult?.data);
    setLoadingReviews(false);
  };

  // Merge live data with mock data
  const displayRating = liveAggregate?.average_rating
    ? parseFloat(liveAggregate?.average_rating)
    : professional?.rating;

  const displayReviewCount = liveAggregate?.total_reviews
    ? liveAggregate?.total_reviews + (professional?.reviews?.length || 0)
    : professional?.reviews?.length || 0;

  // Combine live reviews with mock reviews (live first)
  const allReviews = [
    ...liveReviews?.map(r => ({
      author: r?.reviewer_name,
      rating: r?.rating,
      date: new Date(r?.created_at)?.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
      text: r?.feedback || '',
      isLive: true,
      photos: r?.professional_review_photos || [],
    })),
    ...(professional?.reviews || []),
  ];

  const tabs = [
    { id: 'overview', label: 'Aperçu', icon: 'User' },
    { id: 'portfolio', label: 'Portfolio', icon: 'Briefcase' },
    { id: 'calendar', label: 'Calendrier', icon: 'Calendar' },
    { id: 'reviews', label: `Avis (${displayReviewCount})`, icon: 'Star' },
  ];

  return (
    <div className={`bg-white rounded-2xl transition-all duration-200 overflow-hidden shadow-ios-light hover:shadow-ios-medium ${isSelected ? 'ring-2 ring-brand-earth' : ''}`}>
      {/* Header */}
      <div className="p-5 pb-4">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${professional?.color} flex items-center justify-center flex-shrink-0 shadow-ios-light`}>
            <span className="text-white text-xl font-bold">{professional?.name?.charAt(0)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-display font-display-semibold text-text-primary text-base leading-tight">{professional?.name}</h3>
                <p className="text-xs text-text-secondary mt-0.5">{professional?.title}</p>
              </div>
              {professional?.verified && (
                <span className="flex items-center gap-1 text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full border border-blue-200 flex-shrink-0">
                  <Icon name="BadgeCheck" size={11} />
                  Vérifié
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-2">
              <div className="flex items-center gap-1.5">
                <StarRating rating={displayRating} size={13} />
                <span className="text-xs font-semibold text-text-primary">{displayRating?.toFixed(1)}</span>
              </div>
              <span className="text-xs text-text-secondary">·</span>
              <span className="text-xs text-text-secondary">{professional?.completedJobs} missions</span>
              <span className="text-xs text-text-secondary">·</span>
              <span className="text-xs text-text-secondary">{professional?.experience} ans exp.</span>
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {professional?.tags?.map((tag, i) => (
            <span key={i} className="text-xs px-2.5 py-0.5 bg-surface rounded-full border border-border text-text-secondary">{tag}</span>
          ))}
        </div>

        {/* Price & Response time */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
          <div className="flex items-center gap-1.5 text-xs text-text-secondary">
            <Icon name="Clock" size={12} />
            <span>Répond en {professional?.responseTime}</span>
          </div>
          <div className="text-sm font-semibold text-brand-earth">{professional?.priceRange}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-t border-border/50 bg-surface/30">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex-1 flex items-center justify-center gap-1 py-2.5 text-xs font-medium transition-colors ${activeTab === tab?.id ? 'text-brand-earth border-b-2 border-brand-earth bg-white' : 'text-text-secondary hover:text-text-primary'}`}
          >
            <Icon name={tab?.icon} size={12} />
            <span className="hidden sm:inline">{tab?.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-5 pt-4 min-h-[160px]">
        {/* Overview */}
        {activeTab === 'overview' && (
          <div className="space-y-3">
            <p className="text-sm text-text-secondary leading-relaxed">{professional?.bio}</p>
            <div className="grid grid-cols-2 gap-2">
              {professional?.stats?.map((stat, i) => (
                <div key={i} className="flex items-center gap-2 p-2.5 bg-surface/60 rounded-xl border border-border/50">
                  <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${professional?.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon name={stat?.icon} size={13} color="white" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-text-primary">{stat?.value}</p>
                    <p className="text-xs text-text-secondary leading-tight">{stat?.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Portfolio */}
        {activeTab === 'portfolio' && (
          <div className="space-y-3">
            <p className="text-xs text-text-secondary mb-3">Réalisations récentes</p>
            <div className="space-y-2">
              {professional?.portfolio?.map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-surface/50 rounded-xl border border-border/50 hover:bg-surface transition-colors">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${professional?.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon name={item?.icon} size={14} color="white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary leading-tight">{item?.title}</p>
                    <p className="text-xs text-text-secondary mt-0.5">{item?.description}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-xs text-brand-earth font-medium">{item?.value}</span>
                      <span className="text-xs text-text-secondary/60">·</span>
                      <span className="text-xs text-text-secondary">{item?.date}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Calendar */}
        {activeTab === 'calendar' && (
          <AvailabilityCalendar seed={professional?.calendarSeed} />
        )}

        {/* Reviews */}
        {activeTab === 'reviews' && (
          <div className="space-y-3">
            {loadingReviews ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Rating summary */}
                <div className="flex items-center gap-4 p-3 bg-surface/50 rounded-xl border border-border/50 mb-3">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-text-primary">{displayRating?.toFixed(1)}</p>
                    <StarRating rating={displayRating} size={12} />
                    <p className="text-xs text-text-secondary mt-0.5">{displayReviewCount} avis</p>
                  </div>
                  <div className="flex-1 space-y-1">
                    {[5, 4, 3, 2, 1]?.map((star) => {
                      const liveCount = liveAggregate ? (liveAggregate?.[`rating_${star}_count`] || 0) : 0;
                      const mockCount = professional?.reviews?.filter(r => Math.round(r?.rating) === star)?.length || 0;
                      const count = liveCount + mockCount;
                      const total = displayReviewCount || 1;
                      const pct = (count / total) * 100;
                      return (
                        <div key={star} className="flex items-center gap-2">
                          <span className="text-xs text-text-secondary w-3">{star}</span>
                          <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
                            <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-text-secondary w-4">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Live reviews badge */}
                {liveReviews?.length > 0 && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    <span className="text-xs text-green-600 font-medium">{liveReviews?.length} avis récent{liveReviews?.length > 1 ? 's' : ''} de clients vérifiés</span>
                  </div>
                )}

                {/* Individual reviews */}
                <div className="space-y-2.5">
                  {allReviews?.map((review, i) => (
                    <div key={i} className={`p-3 rounded-xl border ${review?.isLive ? 'bg-green-50/40 border-green-200/60' : 'bg-surface/40 border-border/40'}`}>
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${review?.isLive ? 'bg-gradient-to-br from-green-400 to-emerald-600' : 'bg-gradient-to-br from-slate-400 to-slate-600'}`}>
                            <span className="text-white text-xs font-bold">{review?.author?.charAt(0)}</span>
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs font-semibold text-text-primary">{review?.author}</p>
                              {review?.isLive && (
                                <span className="text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded-full border border-green-200">Vérifié</span>
                              )}
                            </div>
                            <p className="text-xs text-text-secondary">{review?.date}</p>
                          </div>
                        </div>
                        <StarRating rating={review?.rating} size={11} />
                      </div>
                      {review?.text && (
                        <>
                          <p className={`text-xs text-text-secondary leading-relaxed ${expandedReview !== i && review?.text?.length > 100 ? 'line-clamp-2' : ''}`}>
                            {review?.text}
                          </p>
                          {review?.text?.length > 100 && (
                            <button
                              onClick={() => setExpandedReview(expandedReview === i ? null : i)}
                              className="text-xs text-brand-earth mt-1 hover:underline"
                            >
                              {expandedReview === i ? 'Voir moins' : 'Voir plus'}
                            </button>
                          )}
                        </>
                      )}
                      {/* Review photos */}
                      {review?.photos?.length > 0 && (
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {review?.photos?.map((photo, pi) => (
                            <img
                              key={pi}
                              src={reviewService?.getPhotoUrl(photo?.storage_path)}
                              alt={`Photo réalisation ${pi + 1}`}
                              className="w-14 h-14 rounded-lg object-cover border border-border"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Select Button */}
      <div className="px-5 pb-5">
        <button
          onClick={() => onSelect(professional)}
          className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
            isSelected
              ? 'bg-brand-earth text-white shadow-ios-light'
              : 'bg-surface border border-border text-text-primary hover:bg-brand-earth hover:text-white hover:border-brand-earth'
          }`}
        >
          {isSelected ? (
            <><Icon name="CheckCircle2" size={16} />Sélectionné</>
          ) : (
            <><Icon name="UserCheck" size={16} />Choisir ce professionnel</>
          )}
        </button>
      </div>
    </div>
  );
};

export default ProfessionalProfileCard;
